import { EComponentTypes, ISearchLocation, IComponent, IComponentType, IBranch, IdHelper, IProperty, ConditionsHelper, IDecision, PropertyHelper, IComponentDictionary,
    IAction, ComponentHelper, clone, ILoop, EOutputScope } from './';
import store from '../store/store';
import types from '../store/types';

export class ElementsHelper {

    private components: IComponentDictionary;
    private templates;
    private componentTypes;
    private elements;

    constructor(components: IComponentDictionary, templates, componentTypes, elements) {
        this.components = components;
        this.templates = templates;
        this.componentTypes = componentTypes;
        this.elements = elements;
    }

    public AddElement = (searchLocation: ISearchLocation, componentType: IComponentType, passedSubmissionComponent: boolean, id?: string): void => {

        let newComponentId: string = id || IdHelper.newGuid();
        let decisionTemplate: IDecision = ConditionsHelper.getDefaultDecisionComponent(this.templates);
        let properties: IProperty[] = PropertyHelper.getDefaultProperties(componentType, this.templates);
        decisionTemplate.ComponentId = newComponentId;

        let parentDecisionId: string = searchLocation.branchId && searchLocation.branchId.length && searchLocation.branchId !== 'root' ? searchLocation.branchId.split('_')[0] : null;

        if (componentType.ComponentTypeId === EComponentTypes.summary || componentType.ComponentTypeId === EComponentTypes.redirect) {
            let actionId = IdHelper.newGuid();
            let actionComponentType = {
                Alias: "A",
                Allowed: [0, 1, 2, 3, 5, 6],
                Color: "#e81123",
                ComponentTypeId: 1,
                Name: "Action"
            };

            this.AddElement(searchLocation, actionComponentType, passedSubmissionComponent, actionId);

            // the Create Journey Answers template was cached as part of the initialisation > init function
            let createJourneyAnswersTemplate: IAction = store.getState().cacheState.actionTemplates['Create Journey Answers'];

            store.dispatch({
                type: 'UPDATE_ACTION',
                componentId: actionId,
                action: createJourneyAnswersTemplate
            });

            store.dispatch({
                type: types.SET_NEXT_COMPONENT_ID,
                componentId: actionId,
                nextComponentId: newComponentId
            });

            searchLocation.ComponentId = actionId;
            searchLocation.ComponentType = actionComponentType;
        }

        let loopTemplate: ILoop;

        if(componentType.ComponentTypeId === EComponentTypes.loop) {
            
            loopTemplate = clone(ComponentHelper.getComponentTemplate(EComponentTypes.loop)).Loop;
            loopTemplate.ComponentId = newComponentId;
            loopTemplate.OutputValues = loopTemplate.OutputValues.map(o => {
                o.Id = IdHelper.newGuid();
                o.OutputScope = ( o.OutputScope !=  EOutputScope.LoopSystem)  ? EOutputScope.Loop  :  o.OutputScope;
                return {
                    ...o,
                    LoopId: newComponentId
                };
            });

        }

        store.dispatch({
            type: types.ADD_COMPONENT,
            componentType: componentType,
            Id: newComponentId,
            searchLocation: searchLocation,
            properties: properties,
            parentDecisionId: parentDecisionId,
            decisionTemplate: componentType.ComponentTypeId === EComponentTypes.decision
                ? decisionTemplate
                : null,
            loopTemplate: loopTemplate
        });

        store.dispatch({
            type: types.ADD_ACTIVE_COMPONENT,
            previousComponentId: searchLocation.ComponentId,
            loopComponentId: searchLocation.loopComponentId,
            newComponentId: newComponentId,
            componentType: componentType.ComponentTypeId
        });

        let currentSearchComponent: IComponent = this.components[searchLocation.ComponentId];
  
        if (currentSearchComponent) {
            //if the condition branch already has a next component, then use it as the nextComponent for the new component
            //this is relevant if you are adding a new component at the top of a branch

            if (
                currentSearchComponent.ComponentType.ComponentTypeId === EComponentTypes.decision &&
                searchLocation.branchId &&
                searchLocation.branchId !== 'root' &&
                searchLocation.branchId.split('_')[0] === currentSearchComponent.Id //this check will avoid the gotcha of dealing with a sub-condition
            ) {
                this.setBranchFirstElementNextComponent(currentSearchComponent, searchLocation, newComponentId);
            }
            else if (
                currentSearchComponent.ComponentType.ComponentTypeId === EComponentTypes.loop && !!searchLocation.loopComponentId
            ) {
                this.setFirstLoopComponent(currentSearchComponent, newComponentId);
            }

            //if the current search component has a next component, then use it as the nextComponent for the new component
            //this is relevant if you are adding a new component between two existing components
            else {
                this.setBranchElementNextComponent(currentSearchComponent, newComponentId);
            }

        }
      
        store.dispatch({
            type: types.SET_SEARCH_LOCATION,
            searchLocation: {
                branchId: searchLocation.branchId,
                ComponentId: newComponentId,
                ComponentType: componentType,
                loopComponentId: searchLocation.loopComponentId
            }
        });

        store.dispatch({
            type: types.GET_SUGGESTIONS,
            search: '',
            componentTypes: this.componentTypes,
            passedSubmissionComponent: passedSubmissionComponent,
            isSharedJourney: store.getState().toolsState.isSharedJourney
        });

    }

    private setFirstLoopComponent = (currentSearchComponent: IComponent, newComponentId: string) => {
        
        // if currentSearchComponent already has a firstLoopComponent, then use that as the NextComponent for the new firstLoopComponent
        if(currentSearchComponent.Loop.FirstLoopComponent && currentSearchComponent.Loop.FirstLoopComponent.ComponentId) {
            store.dispatch({
                type: types.SET_NEXT_COMPONENT_ID,
                componentId: newComponentId,
                nextComponentId: currentSearchComponent.Loop.FirstLoopComponent.ComponentId
            });
        }

        store.dispatch({
            type: types.SET_FIRST_LOOP_COMPONENT_ID,
            componentId: currentSearchComponent.Id,
            firstLoopComponentId: newComponentId
        });

    }

    private setBranchElementNextComponent = (currentSearchComponent: IComponent, newComponentId: string) => {

        if (currentSearchComponent.NextComponent && currentSearchComponent.NextComponent.ComponentId)
            store.dispatch({
                type: types.SET_NEXT_COMPONENT_ID,
                componentId: newComponentId,
                nextComponentId: currentSearchComponent.NextComponent.ComponentId
            });

        store.dispatch({
            type: types.SET_NEXT_COMPONENT_ID,
            componentId: currentSearchComponent.Id,
            nextComponentId: newComponentId,
        });

    }

    private setBranchFirstElementNextComponent = (currentSearchComponent: IComponent, searchLocation: ISearchLocation, newComponentId: string) => {

        let { branchId } = searchLocation;
        let conditions: IBranch[] = currentSearchComponent.Decision.Branches;

        // Get current branch
        let condition: IBranch = conditions.filter((c, i) => {

            let conditionId: string = IdHelper.getConditionId(searchLocation.ComponentId, i);
            return conditionId === branchId;

        })[0];

        // If branch found, dispatch actions
        if (condition) {

            //if the branch already has a NextComponent, then use that as the NextComponent of the new component
            //this handles the case where you insert a new component at the top of a populated branch
            if (condition.NextComponent && condition.NextComponent.ComponentId) {
                store.dispatch({
                    type: types.SET_NEXT_COMPONENT_ID,
                    componentId: newComponentId,
                    nextComponentId: condition.NextComponent.ComponentId
                });
            }

            store.dispatch({
                type: types.SET_NEXT_COMPONENT_ID_FOR_CONDITION,
                componentId: searchLocation.ComponentId,
                branchId: branchId,
                nextComponentId: newComponentId
            });

        }

    }

}


// WEBPACK FOOTER //
// ./source/ts/helpers/elements.ts